<template>
  <div class="org-details">
    <CCard>
      <CCardBody>
        <CCardTitle>
          <h1 class="mb-4">
            <template v-if="loading">Organization details</template>
            <template v-else
              >Organization "{{ organization.organizationName }}"</template
            >
          </h1>
        </CCardTitle>

        <CRow>
          <CCol col="6">
            <h2>Details</h2>

            <!-- update trial/roles buttons -->
            <div class="actions">
              <CButton
                class="mr-1"
                color="warning"
                variant="outline"
                square
                size="sm"
                :disabled="!organizationAvailable"
                @click="
                  () => {
                    modals.editTrial.show = true;
                  }
                "
              >
                Set Trial Data
              </CButton>
              <CButton
                color="warning"
                variant="outline"
                square
                size="sm"
                :disabled="!organizationAvailable"
                @click="
                  () => {
                    modals.editRoles.show = true;
                  }
                "
              >
                Set Roles
              </CButton>
            </div>

            <!-- editable fields for organization -->
            <div class="organization-parameters">
              <div class="details-row">
                <div class="details-row__title">Organization Id</div>
                <div class="details-row__value">
                  {{ organization.organizationId }}<br />
                </div>
              </div>

              <div class="details-row">
                <div class="details-row__title">Organization name</div>
                <div class="details-row__value">
                  <editable-field
                    fieldType="text"
                    fieldName="organizationName"
                    :entityData="organization"
                    :saveMethod="updateOrg"
                    @updated="() => {}"
                    @update-error="handleOrganizationUpdateError"
                    placeholder="--"
                  />
                </div>
              </div>

              <div class="details-row">
                <div class="details-row__title">Subscription start</div>
                <div class="details-row__value">
                  <editable-field
                    fieldType="datetime"
                    :options="datePickerOptions"
                    fieldName="subscriptionStart"
                    :entityData="organization"
                    :saveMethod="updateOrg"
                    @updated="() => {}"
                    @update-error="handleOrganizationUpdateError"
                    placeholder="--"
                  />
                </div>
              </div>

              <div class="details-row">
                <div class="details-row__title">Subscription end</div>
                <div class="details-row__value">
                  <editable-field
                    fieldType="datetime"
                    :options="datePickerOptions"
                    fieldName="subscriptionEnd"
                    :entityData="organization"
                    :saveMethod="updateOrg"
                    @updated="() => {}"
                    @update-error="handleOrganizationUpdateError"
                    placeholder="--"
                  />
                </div>
              </div>

              <div
                class="details-row"
                v-if="organization && organization.appMetadata"
              >
                <div class="details-row__title">Trial Details</div>
                <div class="details-row__value">
                  Trial Access: {{ organization.appMetadata.trialUser }}
                </div>
              </div>
              <div
                class="details-row"
                v-if="organization && organization.appMetadata"
              >
                <div class="details-row__title">Trial Details</div>
                <div class="details-row__value">
                  Trial Expiry: {{ organization.appMetadata.trialExpiry }}
                </div>
              </div>
              <div v-else>No app metadata defined for this org</div>
              <div class="details-row">
                <div class="details-row__title">Roles:</div>

                <div
                  v-for="(role, index) in organization.accessRoleId"
                  :key="`role-${index}`"
                >
                  <div class="details-row__value">{{ role }}</div>
                </div>
              </div>
            </div>
          </CCol>

          <CCol col="6">
            <h2>Users</h2>
            <TheTable
              class="p-0"
              :items="users.items"
              :fields="users.fields"
              @edit="handleEditUser($event)"
            />
          </CCol>
        </CRow>

        <!--
        <pre>{{ organization }}</pre>
        -->
      </CCardBody>
    </CCard>

    <!-- MODALS -->
    <!-- common errors modal -->
    <error-modal :show.sync="modals.error.show" :errors="this.modalErrors" />

    <!-- add/remove roles modal -->
    <edit-roles-modal
      :organization="organization"
      @updated="handleOrganizationUpdated"
      :show.sync="modals.editRoles.show"
    />

    <!-- edit trial modal -->
    <edit-trial-modal
      :organization="organization"
      @updated="handleOrganizationUpdated"
      :show.sync="modals.editTrial.show"
    />
  </div>
</template>

<script>
import getNested from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import { mapActions } from "vuex";
import TheTable from "./../containers/TheTable";
import EditableField from "@/components/widgets/EditableField";
import EditRolesModal from "@/components/organization/modals/EditRolesModal";
import ErrorModal from "@/components/organization/modals/ErrorModal";
import EditTrialModal from "@/components/organization/modals/EditTrialModal";

export default {
  name: "org-details",

  components: {
    EditTrialModal,
    ErrorModal,
    EditRolesModal,
    EditableField,
    TheTable,
  },

  data() {
    return {
      loading: false,
      organization: {},
      users: {
        items: [],
        fields: [
          { key: "userId", label: "User ID" },
          { key: "userName", label: "User Name" },
          { key: "email", label: "Email" },
          { key: "blocked", label: "Blocked" },
          {
            key: "edit",
            label: "Edit",
            sorter: false,
            filter: false,
            _classes: "text-center",
          },
        ],
      },

      modals: {
        editRoles: {
          show: false,
        },
        editTrial: {
          show: false,
        },
        error: {
          show: false,
        },
      },
      modalErrors: [],
    };
  },

  async mounted() {
    await this.loadOrgData();
    await this.getUsers();
  },

  methods: {
    getNested,

    ...mapActions("users", ["getOrg", "updateOrg", "listUsersForOrg"]),
    ...mapActions(["getUserAuth0"]),

    async loadOrgData() {
      this.loading = true;
      let data = await this.getOrg({ orgId: this.getOrganizationId() });
      this.organization = getNested(data, "organization", []);
      this.loading = false;
    },

    async getUsers() {
      this.loading = true;
      let data = await this.listUsersForOrg({
        orgId: this.getOrganizationId(),
      });
      this.users.items = getNested(data, "users", []);
      this.loading = false;
    },

    async handleEditUser(user) {
      let auth0User = await this.getUserAuth0(user.userId);
      console.log("handle edit user", JSON.stringify(auth0User, null, 2));
      this.$router.push({ name: "Edit User", params: { user: auth0User } });
    },

    getOrganizationId() {
      return getNested(this, "$route.params.org_id", null);
    },

    handleUpdateOrganization(data) {
      console.log("handle update org", data);
    },

    async handleOrganizationUpdateError(response) {
      if (response.error) {
        const errorDetails = getNested(
          response,
          "error_details",
          "Unknown error"
        );
        if (Array.isArray(errorDetails)) {
          this.modalErrors = errorDetails.map((item) => JSON.stringify(item));
        } else {
          this.modalErrors = [JSON.stringify(errorDetails)];
        }
        this.modals.error.show = true;
      }
      await this.loadOrgData();
    },

    async handleOrganizationUpdated() {
      await this.loadOrgData();
    },
  },

  computed: {
    datePickerOptions() {
      return {
        displayFormat: `y-LL-dd HH:mm:ss`,
        saveFormat: `y-LL-dd'T'HH:mm:ss'Z'`,
      };
    },

    organizationAvailable() {
      return !isEmpty(this.organization) && this.organization.organizationId;
    },
  },
};
</script>

<style scoped lang="scss">
.org-details {
  .organization-parameters {
    margin-top: 30px;
  }

  .actions {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }

  .details-row {
    // padding-bottom: 5px;
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px dotted #d0d0d0;

    &__title {
      min-width: 100px;
      padding-right: 10px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &__value {
      padding-left: 10px;
      font-weight: 500;
      font-size: 15px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
